import React, { useCallback } from 'react';
import { cssMap } from '@atlaskit/css';
import Button from '@atlaskit/button/new';
import { Flex, Inline, Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useActionsCommandPalette } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import { isMacOs } from '@atlassian/jira-common-components-keyboard-shortcuts-dialog/src/utils.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export const CommandPaletteSearchButton = () => {
	const { formatMessage } = useIntl();
	const { open } = useActionsCommandPalette();
	const handleSearchButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			event.preventDefault();
			open();

			fireUIAnalytics(analyticsEvent, 'globalSettingsCommandPaletteSearchButton');
		},
		[open],
	);
	return (
		<Box>
			<Button
				aria-label={formatMessage(
					isMacOs() ? messages.labelForMacScreenReader : messages.labelForOtherScreenReader,
				)}
				onClick={handleSearchButtonClick}
			>
				{formatMessage(isMacOs() ? messages.labelForMac : messages.labelForOther, {
					wrapper: (chunks: React.ReactNode) => (
						<Inline xcss={styles.commandPalleteShortcutStyles} space="space.100">
							{chunks}
						</Inline>
					),
					key: (chunks: React.ReactNode) => (
						<Flex alignItems="center" justifyContent="center" xcss={styles.keyboardShortcutStyles}>
							{chunks}
						</Flex>
					),
					command: (chunks: React.ReactNode) => (
						<Flex alignItems="center" justifyContent="center" xcss={styles.keyboardShortcutStyles}>
							{chunks}
						</Flex>
					),
				})}
			</Button>
		</Box>
	);
};

const styles = cssMap({
	commandPalleteShortcutStyles: {
		display: 'inline-flex',
		alignItems: 'center',
	},
	keyboardShortcutStyles: {
		minWidth: '14px',
		height: '14px',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		color: token('color.text.subtle'),
		paddingTop: token('space.025'),
		paddingRight: token('space.025'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.025'),
		borderRadius: token('border.radius'),
		font: token('font.body.small'),
	},
});
